import React, { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

import { Blurb } from './types';

interface WalkthroughContextValue {
  queuedBlurbs: Blurb[];

  queueBlurbs: (blurbs: Blurb[]) => void;

  progressWalkthrough: () => void;
  exitWalkthrough: () => void;

  events: EventTarget;
}

const WalkthroughContext = createContext<WalkthroughContextValue>({
  queuedBlurbs: [],
  queueBlurbs: () => {},
  progressWalkthrough: () => {},
  exitWalkthrough: () => {},
  events: new EventTarget(),
});

export function useWalkthroughContext() {
  return useContext(WalkthroughContext);
}

export function useActiveBlurb(): Blurb | null {
  return useWalkthroughContext().queuedBlurbs[0] ?? null;
}

export function WalkthroughContextProvider(props: PropsWithChildren) {
  const [queuedBlurbs, setQueuedBlurbs] = useState<Blurb[]>([]);

  const [events] = useState(() => new EventTarget());

  const queueBlurbs = useCallback(
    (blurbs: Blurb[]) => {
      setQueuedBlurbs((existingBlurbs) => [...existingBlurbs, ...blurbs]);
    },
    [setQueuedBlurbs],
  );

  const progressWalkthrough = useCallback(() => {
    const nextBlurb = queuedBlurbs[1];

    if (nextBlurb == null) {
      events.dispatchEvent(new Event('finish'));
      return;
    }

    const moveToNext = (blurbs: Blurb[]) => blurbs.slice(1);

    if (nextBlurb.beforeShow) {
      nextBlurb.beforeShow(() => setQueuedBlurbs(moveToNext));
    } else {
      setQueuedBlurbs(moveToNext);
    }
  }, [events, setQueuedBlurbs, queuedBlurbs]);

  const exitWalkthrough = useCallback(() => {
    setQueuedBlurbs([]);
    events.dispatchEvent(new Event('finish'));
  }, [events, setQueuedBlurbs]);

  const value = useMemo(
    () => ({
      queuedBlurbs,

      queueBlurbs,
      progressWalkthrough,
      exitWalkthrough,

      events,
    }),

    [queuedBlurbs, queueBlurbs, progressWalkthrough, exitWalkthrough, events],
  );

  return <WalkthroughContext.Provider value={value}>{props.children}</WalkthroughContext.Provider>;
}
